import React, { useState, useEffect, useCallback } from 'react';
import {
  Page,
  Layout,
  Card,
  TextField,
  Text,
  Frame,
  Toast,
  Spinner,
  Box,
  BlockStack,
  InlineStack,
  Banner,
  Checkbox,
  Tabs,
} from '@shopify/polaris';
import { api } from '../api';

const TAB_CONFIG = [
  { id: 'customer_confirmation', content: 'Customer email' },
  { id: 'salesperson_rfq', content: 'Salesperson email' },
  { id: 'cc_rfq', content: 'CC email' },
  { id: 'rfq_details_block', content: 'Quote details block' },
  { id: 'contact_us_salesperson', content: 'Contact Us email' },
];

const PLACEHOLDER_HELP =
  'Use {{placeholders}} in subject and body. Include {{rfq_details_block}} in full emails where quote details should appear.';

export default function EmailTemplatesPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0);
  const [templates, setTemplates] = useState([]);
  const [form, setForm] = useState({
    subject: '',
    body_html: '',
    body_text: '',
    is_active: true,
  });
  const [toastActive, setToastActive] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  const activeKey = TAB_CONFIG[selectedTab]?.id;
  const isDetailsBlock = activeKey === 'rfq_details_block';

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/email-templates');
      const list = res.data?.templates || [];
      setTemplates(list);
    } catch (err) {
      setToastMessage(err?.response?.data?.error || err.message || 'Failed to load templates');
      setToastActive(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  useEffect(() => {
    const t = templates.find((x) => x.template_key === activeKey);
    if (t) {
      setForm({
        subject: t.subject || '',
        body_html: t.body_html || '',
        body_text: t.body_text || '',
        is_active: t.is_active !== false,
      });
    }
  }, [templates, activeKey]);

  const activeTemplate = templates.find((x) => x.template_key === activeKey);

  const handleSave = async () => {
    if (!activeKey) return;
    setSaving(true);
    try {
      await api.put(`/api/email-templates/${activeKey}`, form);
      setToastMessage('Template saved');
      setToastActive(true);
      await fetchTemplates();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const base = err?.response?.data?.error || err.message || 'Save failed';
      setToastMessage(detail ? `${base}: ${detail}` : base);
      setToastActive(true);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!activeKey) return;
    if (!window.confirm('Reset this template to the default content?')) return;
    setSaving(true);
    try {
      await api.post(`/api/email-templates/${activeKey}/reset`);
      setToastMessage('Template reset to default');
      setToastActive(true);
      await fetchTemplates();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const base = err?.response?.data?.error || err.message || 'Reset failed';
      setToastMessage(detail ? `${base}: ${detail}` : base);
      setToastActive(true);
    } finally {
      setSaving(false);
    }
  };

  const toastMarkup = toastActive ? (
    <Toast content={toastMessage} onDismiss={() => setToastActive(false)} />
  ) : null;

  return (
    <Frame>
      {toastMarkup}
      <Page
        title="Email templates"
        primaryAction={{
          content: 'Save template',
          onAction: handleSave,
          loading: saving,
          disabled: loading,
        }}
        secondaryActions={[
          {
            content: 'Reset to default',
            onAction: handleReset,
            disabled: loading || saving,
          },
        ]}
      >
        <Layout>
          <Layout.Section>
            <Banner tone="info">
              <p>
                {activeKey === 'rfq_details_block' ? (
                  <>
                    Edit the shared <strong>quote details</strong> section (country, customer
                    fields, products). Use it in customer, salesperson, and CC emails via{' '}
                    <strong>{'{{rfq_details_block}}'}</strong> in their HTML body. Product line
                    items are inserted at <strong>{'{{products_html}}'}</strong>; optional rows use{' '}
                    <strong>{'{{company_row}}'}</strong>, <strong>{'{{phone_row}}'}</strong>, etc.
                  </>
                ) : activeKey === 'contact_us_salesperson' ? (
                  <>
                    Sent to the assigned salesperson (and CC) when someone submits the Contact Us
                    form. Edit the full HTML here (same idea as the Salesperson email tab for RFQ).
                  </>
                ) : (
                  <>
                    These templates control the customer confirmation, salesperson notification, and
                    CC copy. Include <strong>{'{{rfq_details_block}}'}</strong> where quote details
                    should appear (configure that block on the Quote details block tab).
                  </>
                )}
              </p>
            </Banner>
          </Layout.Section>

          <Layout.Section>
            <Card>
              <Tabs tabs={TAB_CONFIG} selected={selectedTab} onSelect={setSelectedTab}>
                <Box padding="400">
                  {loading ? (
                    <InlineStack align="center">
                      <Spinner size="large" />
                    </InlineStack>
                  ) : (
                    <BlockStack gap="400">
                      {activeTemplate?.description && (
                        <Text as="p" tone="subdued">
                          {activeTemplate.description}
                        </Text>
                      )}

                      <Checkbox
                        label="Template active"
                        checked={form.is_active}
                        onChange={(checked) =>
                          setForm((f) => ({ ...f, is_active: checked }))
                        }
                      />

                      {!isDetailsBlock && (
                        <TextField
                          label="Email subject"
                          value={form.subject}
                          onChange={(value) => setForm((f) => ({ ...f, subject: value }))}
                          autoComplete="off"
                          helpText={PLACEHOLDER_HELP}
                        />
                      )}

                      <TextField
                        label={isDetailsBlock ? 'Quote details HTML' : 'HTML body'}
                        value={form.body_html}
                        onChange={(value) => setForm((f) => ({ ...f, body_html: value }))}
                        multiline={isDetailsBlock ? 20 : 16}
                        autoComplete="off"
                        helpText={
                          isDetailsBlock
                            ? 'HTML fragment only (no <html> wrapper). Keep {{products_html}} for the product list. Empty optional fields use row placeholders like {{company_row}}.'
                            : activeKey === 'contact_us_salesperson'
                              ? 'Full HTML email. Use {{placeholders}} for form fields; optional rows: {{phone_row}}, {{message_row}}, etc.'
                              : 'Full HTML email. Include {{rfq_details_block}} where quote details should appear.'
                        }
                      />

                      {(activeKey === 'customer_confirmation' ||
                        activeKey === 'contact_us_salesperson') && (
                        <TextField
                          label="Plain-text body (optional)"
                          value={form.body_text || ''}
                          onChange={(value) =>
                            setForm((f) => ({ ...f, body_text: value }))
                          }
                          multiline={8}
                          autoComplete="off"
                          helpText="Used as the text/plain fallback for email clients that do not support HTML."
                        />
                      )}

                      <Text variant="headingSm" as="h3">
                        Available placeholders
                      </Text>
                      <Text as="p" tone="subdued">
                        {(activeTemplate?.placeholders || []).join(', ')}
                      </Text>
                    </BlockStack>
                  )}
                </Box>
              </Tabs>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    </Frame>
  );
}
